import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Chip, DialogActions, DialogContent, Typography } from "@mui/material"
import type { FC } from "react"
import { useCallback, useMemo, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { useCreateInventoryAdjustment } from "./hooks/useCreateInventoryAdjustment"
import {
  type SelectedLot,
  useInventoryAdjustmentPalletGroupSelection,
} from "./hooks/useInventoryAdjustmentPalletGroupSelection"
import { useInventoryAdjustmentSyncStatus } from "./hooks/useInventoryAdjustmentSyncStatus"
import { InventoryAdjustmentPalletsSelectionSection } from "./InventoryAdjustmentPalletsSelectionSection"
import { InventoryAdjustmentQuantityDetailsForm } from "./InventoryAdjustmentQuantityDetailsForm"
import { InventoryAdjustmentQuantityForm } from "./InventoryAdjustmentQuantityForm"
import { InventoryAdjustmentUntestedLotsDialog } from "./InventoryAdjustmentUntestedLotsDialog"
import { type InventoryAdjustmentQuantityFormValues, inventoryAdjustmentQuantityFormSchema } from "./schemas"
import { getInventoryAdjustmentUntestedLots } from "./utils"

import { Button } from "@/components/common/Button"
import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import {
  type InventoryAdjustmentReason,
  InventoryAdjustmentType,
  type InventoryByProductWarehouse,
  type Product,
} from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"

interface InventoryAdjustmentQuantityStepProps {
  product: Product
  defaultWarehouseAreaId: string
  warehouseInventories: InventoryByProductWarehouse[]
  warehouseOptions: Option<string>[]
  onCancel: () => void
  onSuccess: () => void
}

export const InventoryAdjustmentQuantityStep: FC<InventoryAdjustmentQuantityStepProps> = ({
  product,
  defaultWarehouseAreaId,
  warehouseInventories,
  warehouseOptions,
  onCancel,
  onSuccess,
}) => {
  const [newUomQtyByLotId, setNewUomQtyByLotId] = useState<Record<string, number | undefined>>({})
  const [untestedLots, setUntestedLots] = useState<SelectedLot[]>([])

  const { createInventoryAdjustment, isCreating } = useCreateInventoryAdjustment()
  const { isSyncing } = useInventoryAdjustmentSyncStatus(product.productId)

  const uomName = product.packageUsWeightUomDetails?.name ?? ""

  const methods = useForm<InventoryAdjustmentQuantityFormValues>({
    resolver: zodResolver(inventoryAdjustmentQuantityFormSchema),
    defaultValues: {
      warehouseAreaId: defaultWarehouseAreaId,
      adjustmentReason: undefined,
      comment: "",
      document: undefined,
    },
    mode: "onChange",
  })

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid, errors },
  } = methods

  const selectedWarehouseAreaId = watch("warehouseAreaId")
  const selectedWarehouseArea = useMemo(
    () =>
      warehouseInventories.find((inventory) => inventory.warehouseArea?.warehouseAreaId === selectedWarehouseAreaId)
        ?.warehouseArea,
    [warehouseInventories, selectedWarehouseAreaId]
  )

  const {
    palletGroups,
    isFetching,
    selectedLotIds,
    selectedLots,
    sortBy,
    sortDirection,
    handleSearchChange,
    handleSortChange,
    handleSelectionChange,
    handleSelectionRemove,
    handleSelectionReset,
  } = useInventoryAdjustmentPalletGroupSelection({
    productExternalSystemId: product.externalSystemId!,
    warehouseAreaExternalSystemId: selectedWarehouseArea!.externalSystemId!,
  })

  const handleQuantityChange = useCallback((lotId: string, qty: number | undefined) => {
    setNewUomQtyByLotId((prev) => ({ ...prev, [lotId]: qty }))
  }, [])

  const handleLotRemove = useCallback(
    (lotId: string) => {
      handleSelectionRemove(lotId)
      setNewUomQtyByLotId((prev) => {
        const next = { ...prev }
        delete next[lotId]
        return next
      })
    },
    [handleSelectionRemove]
  )

  const handleWarehouseChange = () => {
    handleSelectionReset()
    setNewUomQtyByLotId({})
  }

  const canSubmit =
    isValid &&
    !errors.document &&
    selectedLots.length > 0 &&
    selectedLots.every((sl) => {
      const newQty = newUomQtyByLotId[sl.lot.rawInventoryId]
      // newQty must be defined (not undefined = invalid or empty), and different from the original
      return newQty !== undefined && newQty !== sl.lot.totalUnits
    })

  const submitForm = async (formValues: InventoryAdjustmentQuantityFormValues) => {
    if (!formValues.warehouseAreaId) return

    const blockingUntestedLots = getInventoryAdjustmentUntestedLots(selectedLots)
    if (blockingUntestedLots.length) {
      setUntestedLots(blockingUntestedLots)
      return
    }

    const success = await createInventoryAdjustment({
      adjustmentType: InventoryAdjustmentType.AdjustQuantity,
      warehouseAreaId: formValues.warehouseAreaId,
      productId: product.productId,
      reason: formValues.adjustmentReason as unknown as InventoryAdjustmentReason,
      lots: selectedLots.map(({ lot }) => ({
        rawInventoryId: lot.rawInventoryId,
        newUomQty: newUomQtyByLotId[lot.rawInventoryId],
      })),
      comment: formValues.comment,
      document: formValues.document,
    })

    if (success) {
      onSuccess()
    }
  }

  return (
    <FormProvider {...methods}>
      {/* Sub-header: title + product chip + warehouse select */}
      <Box className='flex items-center gap-x-6 border-b border-gray-200 bg-gray-100 px-6 py-3'>
        <Typography variant='h6' className='shrink-0 font-normal text-gray-800'>
          Adjust Quantity
        </Typography>
        <Box className='flex items-center gap-x-2'>
          <Typography variant='body2' className='shrink-0 text-gray-700'>
            Product:
          </Typography>
          <Chip label={product.companyProductDescription} size='small' className='rounded-sm bg-gray-300' />
        </Box>
        <Box className='flex flex-1 items-center gap-x-2'>
          <Typography variant='body2' className='shrink-0 text-gray-700'>
            Warehouse:
          </Typography>
          <FormSelect
            control={control}
            name='warehouseAreaId'
            className='max-w-[560px] flex-1'
            options={warehouseOptions}
            onChange={handleWarehouseChange}
            data-testid='inventory-adjustment.quantity-step.warehouse-select'
          />
        </Box>
      </Box>

      {/* Blue info banner */}
      <Box className='border-y border-[#004B8D] bg-blue-50 px-6 py-3'>
        <Typography variant='body2' className='font-medium text-gray-700'>
          Select one or more pallets to adjust quantities.
        </Typography>
      </Box>

      <DialogContent className='p-6'>
        <Box className='grid min-h-[480px] grid-cols-[1fr_320px] gap-x-6'>
          {/* Left column: sections 1 & 2 */}
          <Box className='flex min-w-0 flex-col gap-y-4'>
            <InventoryAdjustmentPalletsSelectionSection
              palletGroups={palletGroups}
              isFetching={isFetching}
              selectedLotIds={selectedLotIds}
              sortBy={sortBy}
              sortDirection={sortDirection}
              isSyncing={isSyncing}
              uomName={uomName}
              onSelectionChange={handleSelectionChange}
              onSortChange={handleSortChange}
              onSearchChange={handleSearchChange}
            />
            <InventoryAdjustmentQuantityForm
              selectedLots={selectedLots}
              newUomQtyByLotId={newUomQtyByLotId}
              uomName={uomName}
              onQuantityChange={handleQuantityChange}
              onLotRemove={handleLotRemove}
            />
          </Box>
          <InventoryAdjustmentQuantityDetailsForm />
        </Box>
      </DialogContent>

      <DialogActions className='justify-between px-10 pb-10'>
        <Button appearance='outlined' onClick={onCancel} data-testid='inventory-adjustment.quantity-step.cancel-button'>
          Cancel
        </Button>
        <Button
          appearance='contained'
          disabled={!canSubmit}
          loading={isCreating}
          onClick={handleSubmit(submitForm)}
          data-testid='inventory-adjustment.quantity-step.submit-button'
        >
          Confirm Adjustment
        </Button>
      </DialogActions>

      {untestedLots.length > 0 && (
        <InventoryAdjustmentUntestedLotsDialog untestedLots={untestedLots} onClose={() => setUntestedLots([])} />
      )}
    </FormProvider>
  )
}
